import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { Layout, message } from 'antd';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { PlayerProvider } from '@context/PlayerContext';
import MediaViewerHeader from '@components/MediaViewerHeader';
import {
  removeComment,
  editComment,
  setCommentLike,
  setAssetVariant,
  setAsset,
  setCommentResolve
} from '@redux/actions/mediaViewerAction';
import {
  createCommentAssetReview,
  createReplyCommentAssetReview,
  deleteCommentAssetReview,
  getCommentsAssetReview,
  getPotentialMentionsAssetReview,
  assetReviewCommentsMarkAssetCommentsRead,
  setCommentLikeAssetReview,
  setCommentResolveAssetReview,
  updateCommentAssetReview
} from '@api/AssetReview';
import {
  ReplyAssetReviewCommentDto,
  CreateAssetReviewCommentDto,
  GetAssetVersionCommentsSuccessDto
} from '@api/Api';
import { useShareReview } from '@components/ShareReviewProvider';
import { useTypedSelector } from '@hooks';
import { nonNull } from '@helpers/non-null';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import { apiClient } from '@api/client';
import { useCommentsAssetReviewQuery } from '@hooks/queries/assetReview';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useAssetByReviewQuery } from '@hooks/queries/assets';
import { useLayoutEffectWithPrev } from '@hooks/useEffectWithPrev';
import { showNewAssetVersionToast } from '@helpers/showNewAssetVersionToast';
import { QUERY_COMMENTS_ASSETS } from '@hooks/queries/asset';
import { useResponsive, useResetMobileViewport } from '@hooks/useResponsive';
import { useThemeColor } from '@hooks/useThemeColor';
import MediaViewerTourBanner from '@components/MediaViewerTourBanner/MediaViewerTourBanner';

const { Header, Content } = Layout;

function ExternalReviewerMediaLayout() {
  useResetMobileViewport();

  const dispatch = useDispatch();
  const { isLgWidth } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const { content, reviewId, password } = useShareReview();
  const assetIdParam = searchParams.get('assetId');
  const versionIdParam = searchParams.get('versionId');
  const assetQuery = useAssetByReviewQuery(
    { assetId: assetIdParam ?? '', reviewId: reviewId ?? '', password },
    {
      enabled: !!assetIdParam,
      placeholderData: content?.assets.find((x) => x.id === assetIdParam)
    }
  );
  const asset = assetQuery.data ?? null;
  const assetId = asset?.id;
  const hasApproval = !!asset?.asset?.approvalWorkflow;
  const prevVersionId = useRef(selectedVersion?.id || null);
  const backgroundColor = isLgWidth ? '#1A1C1E' : '#000';

  useThemeColor(backgroundColor);

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useCommentsAssetReviewQuery(
      {
        assetVersionId: selectedVersion?.id || '',
        assetReviewId: reviewId || '',
        assetReviewPassword: password
      },
      {
        keepPreviousData: selectedVersion?.id === prevVersionId.current,
        enabled: !!selectedVersion?.id && !!reviewId
      }
    );
  const commentsLastReadAt = commentsList?.lastReadAt ?? null;
  const commentsItems = commentsList?.comments ?? [];

  if (selectedVersion?.id !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = selectedVersion?.id || null;
  }

  const outletContext = useMemo<MediaViewerOutletContextState>(() => {
    const params = {
      assetReviewId: nonNull(reviewId),
      assetReviewPassword: password
    };
    return {
      fetchCommentsList: async ({ assetVersionId }) => {
        const { data } = await getCommentsAssetReview({
          assetVersionId,
          assetReviewId: reviewId || '',
          assetReviewPassword: password
        });
        return data;
      },
      addNewComment: async (data, parentId) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await createReplyCommentAssetReview({
              ...(data as ReplyAssetReviewCommentDto),
              ...params
            });
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await createCommentAssetReview({
              ...(data as CreateAssetReviewCommentDto),
              ...params
            });
            newComment = comment;
          }
          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id) => {
        try {
          await removeComment(id);
          await deleteCommentAssetReview({ id, ...params });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data) => {
        try {
          const {
            data: { comment }
          } = await updateCommentAssetReview({
            ...data,
            ...params
          });
          await editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data, user) => {
        try {
          await setCommentLike(data, user);
          await setCommentLikeAssetReview({
            ...data,
            ...params
          });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentResolve: async (data, user) => {
        await setCommentResolve(data, user);
        await setCommentResolveAssetReview({
          ...data,
          ...params
        });
      },
      getPotentialMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } = await getPotentialMentionsAssetReview({
          assetVersionId,
          searchQuery,
          ...params
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } =
          await apiClient.assetReview.assetReviewCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery,
              ...params
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsItems,
      commentsLastReadAt,
      markCommentsRead: async () => {
        const selectedVersionId = selectedVersion?.id;
        if (!selectedVersionId || !reviewId) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_COMMENTS_ASSETS, selectedVersionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await assetReviewCommentsMarkAssetCommentsRead({
          assetVersionId: selectedVersionId,
          assetReviewId: reviewId,
          assetReviewPassword: password
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_COMMENTS_ASSETS, selectedVersionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      },
      tasksLastReadAt: null,
      latestTaskCreatedAt: null,
      markTasksRead: async () => {
        // No-op for external reviewers as they don't have access to tasks
      },
      mintWebsiteProxyGrant: () => ({
        assetId: nonNull(assetId),
        assetVersionId: nonNull(selectedVersion?.id),
        target: {
          type: 'review',
          id: nonNull(reviewId),
          password
        }
      })
    };
  }, [
    reviewId,
    password,
    isLoadingCommentsList,
    commentsItems,
    commentsLastReadAt,
    assetId,
    selectedVersion?.id,
    queryClient
  ]);

  useLayoutEffect(() => {
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_STATE
    });
  }, [assetIdParam, dispatch]);

  useLayoutEffectWithPrev(
    ([prevAsset, prevVersionId]) => {
      dispatch(setAsset(asset));
      const version =
        asset?.asset?.versions.find((v) => v.id === versionIdParam) ??
        asset?.asset?.versions[0];
      dispatch(setAssetVariant(version?.id ?? null));

      let newVersionAdded = false;
      let newVersionAddedByMe = false;
      if (prevAsset?.id === asset?.id) {
        const optimisticUpdate =
          !!(prevAsset as any)?.versionsOptimisticUpdate ||
          !!(asset as any)?.versionsOptimisticUpdate;
        const prevVersionIds = (prevAsset?.asset?.versions ?? []).map(
          (v) => v.id
        );
        const newVersionIds = (asset?.asset?.versions ?? []).map((v) => v.id);
        newVersionAdded =
          !optimisticUpdate &&
          newVersionIds[0] !== prevVersionId &&
          newVersionIds.some((id) => !prevVersionIds.includes(id));
        if (newVersionAdded) {
          const newVersion = asset?.asset?.versions.find(
            (v) => !prevVersionIds.includes(v.id)
          );
          newVersionAddedByMe = !!newVersion?.user?.me;
        }
      }

      if (asset && version?.id !== versionIdParam) {
        const version = asset?.asset?.versions[0];
        setSearchParams(
          (prev) => {
            prev.set('versionId', version?.id ?? '');
            return prev;
          },
          { replace: true }
        );
      } else if (newVersionAdded) {
        const goToLatestVersion = () =>
          setSearchParams((prev) => {
            prev.delete('versionId');
            return prev;
          });
        if (newVersionAddedByMe) goToLatestVersion();
        else showNewAssetVersionToast(nonNull(assetId), goToLatestVersion);
      }
    },
    [asset, versionIdParam, dispatch]
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== asset?.id) return false;
    if (msg.payload.assetVersionId !== selectedVersion?.id) return false;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, selectedVersion?.id]
    });
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== asset?.id) return false;
    if (msg.payload.assetVersionId !== selectedVersion?.id) return false;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, selectedVersion?.id]
    });
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== asset?.id) return;
      await queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, selectedVersion?.id]
      });
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== asset?.id) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== asset?.id) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, selectedVersion?.id]
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, selectedVersion?.id]
      });
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== asset?.id) return false;
      if (msg.payload.assetVersionId !== selectedVersion?.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    () => assetQuery.refetch(),
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  return (
    <>
      {hasApproval && <MediaViewerTourBanner />}
      <Layout
        style={{
          background: backgroundColor,
          color: '#fff',
          minHeight:
            'calc(var(--viewport-min-height, 100vh) - var(--demo-mode-panel-width, 0px))'
        }}
      >
        <Layout
          className="site-layout external-reviewer-media"
          style={{ background: backgroundColor }}
        >
          <Header
            className="site-layout-background b-background-black"
            style={{
              padding: 0,
              width: '100%'
            }}
          >
            <MediaViewerHeader view="external-share-asset" />
          </Header>
          <Content style={{ marginTop: 70 }}>
            <PlayerProvider>
              <Outlet context={outletContext} />
            </PlayerProvider>
          </Content>
        </Layout>
      </Layout>
    </>
  );
}

export default ExternalReviewerMediaLayout;
